/**
 * mSTEM UC-43 / UC-47 "Quản lý ... mở rộng" manager — shared presentational
 * component for listing user-declared extension DEVICES (mstem.customDevices)
 * and BLOCKS (mstem.customBlocks). MOCK-ONLY; all CRUD wiring lives in the two
 * thin containers (ext-device-manager-modal.jsx / ext-block-manager-modal.jsx).
 *
 * Layout mirrors RobotManagerModal: navy <Modal> shell + a search box (UC-43/47
 * S-1) + an <MstemTable> of rows with per-row "Sửa" (S-2) and "Xóa" (S-3)
 * actions, plus an empty-state when the declared list is empty.
 *
 * Pure props-driven (no Redux, no VM). All copy via react-intl under
 * gen.ext.manage.* (VN default). Columns are passed in by the container so the
 * device/block-specific labels stay co-located with their CRUD logic.
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import MstemTable from '../mstem-table/mstem-table.jsx';
import ExtManagerActionsCell from './ext-manager-actions-cell.jsx';

import styles from './ext-manager-modal.css';

const messages = defineMessages({
    searchPlaceholder: {
        defaultMessage: 'Tìm theo tên…',
        description: 'UC-43/UC-47 manager search box placeholder (S-1)',
        id: 'gen.ext.manage.searchPlaceholder'
    },
    colActions: {
        defaultMessage: 'Thao tác',
        description: 'UC-43/UC-47 manager table column — actions',
        id: 'gen.ext.manage.colActions'
    },
    edit: {
        defaultMessage: 'Sửa',
        description: 'UC-43/UC-47 manager edit row button',
        id: 'gen.ext.manage.edit'
    },
    del: {
        defaultMessage: 'Xóa',
        description: 'UC-43/UC-47 manager delete row button',
        id: 'gen.ext.manage.delete'
    },
    close: {
        defaultMessage: 'Đóng',
        description: 'UC-43/UC-47 manager close button',
        id: 'gen.ext.manage.close'
    }
});

class ExtManagerModalComponent extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, ['handleSearchChange', 'renderActionsCell']);
    }
    handleSearchChange (e) {
        this.props.onSearchChange(e.target.value);
    }
    renderActionsCell (row) {
        const {intl} = this.props;
        return (
            <ExtManagerActionsCell
                deleteLabel={intl.formatMessage(messages.del)}
                editLabel={intl.formatMessage(messages.edit)}
                row={row}
                onDelete={this.props.onClickDelete}
                onEdit={this.props.onClickEdit}
            />
        );
    }
    buildColumns () {
        const {intl, columns} = this.props;
        return columns.concat([{
            key: 'actions',
            label: intl.formatMessage(messages.colActions),
            width: '140px',
            render: this.renderActionsCell
        }]);
    }
    filteredRows () {
        const {rows, searchValue} = this.props;
        const q = (searchValue || '').trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r => (r.name || '').toLowerCase().indexOf(q) !== -1);
    }
    render () {
        const {emptyMessage, intl, subtitle, title, onRequestClose} = this.props;
        return (
            <Modal
                className={styles.modalContent}
                contentLabel={title}
                onRequestClose={onRequestClose}
            >
                <div className={styles.body}>
                    <h2 className={styles.title}>{title}</h2>
                    <p className={styles.subtitle}>{subtitle}</p>
                    <div className={styles.toolbar}>
                        <input
                            className={styles.searchInput}
                            placeholder={intl.formatMessage(messages.searchPlaceholder)}
                            type="text"
                            value={this.props.searchValue}
                            onChange={this.handleSearchChange}
                        />
                    </div>
                    <MstemTable
                        columns={this.buildColumns()}
                        data={this.filteredRows()}
                        emptyMessage={emptyMessage}
                    />
                    <div className={styles.footer}>
                        <button
                            className={styles.btnSecondary}
                            type="button"
                            onClick={onRequestClose}
                        >
                            {intl.formatMessage(messages.close)}
                        </button>
                    </div>
                </div>
            </Modal>
        );
    }
}

ExtManagerModalComponent.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.node.isRequired,
        render: PropTypes.func,
        width: PropTypes.string
    })).isRequired,
    emptyMessage: PropTypes.node.isRequired,
    intl: intlShape.isRequired,
    onClickDelete: PropTypes.func.isRequired,
    onClickEdit: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
    searchValue: PropTypes.string.isRequired,
    subtitle: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired
};

export default injectIntl(ExtManagerModalComponent);
